/**
 * inquiryLocation.js
 *
 * Reads an inquiry's free-text Location into the project registry's `city` and
 * `country` columns.
 *
 * ── Why this needs parsing at all ──────────────────────────────────────────
 * `inquiries.location_city` is a single free-text box, and eight years of use
 * have filled it with four different kinds of thing:
 *
 *   "Mumbai"                              a bare city — the common case (74 of them)
 *   "Bali, Indonesia"                     city and country
 *   "Vijayawada, Andhra Pradesh, India"   city, state and country
 *   "22°2'46\" N 80°33'53\" E"            coordinates typed into the wrong box
 *
 * The project registry keeps city and country apart, because press-kit
 * filenames render them as one `City-Country` field and a comma in either
 * would break the naming protocol's separators.
 *
 * ── What it will and will not guess ────────────────────────────────────────
 * The country is only ever taken from a segment that IS a country name. It is
 * never inferred from the city: an "Alibaug" inquiry does not become India
 * here, because the same field also holds Amsterdam, Oslo, Seattle and Kuwait,
 * and a wrong country is worse than a blank one — a blank is visibly missing
 * on the overview page, while "India" beside Oslo reads as checked.
 *
 * Coordinates are dropped rather than carried. They belong in the project's
 * Address field, and putting them in `city` would undo the whole point of
 * having a readable location.
 *
 * Anything left is passed through as typed, prose and all ("Office space in
 * Mumbai" stays that). It is a starting value on a field that is editable in
 * one click on the overview page; guessing harder would only make the wrong
 * answers less obvious.
 */

// Country names as they should be stored, lower-cased for matching. This is the
// full ISO set rather than the handful the inquiry data happens to contain —
// the list is only consulted, never enumerated, so completeness costs nothing
// and an inquiry from anywhere is read correctly the first time.
const COUNTRY_NAMES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia',
  'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina',
  'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia',
  'Cameroon', 'Canada', 'Cape Verde', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros',
  'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark',
  'Djibouti', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Estonia',
  'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia',
  'Germany', 'Ghana', 'Greece', 'Guatemala', 'Guinea', 'Guyana', 'Haiti', 'Honduras',
  'Hong Kong', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya',
  'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya',
  'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia',
  'Maldives', 'Mali', 'Malta', 'Mauritania', 'Mauritius', 'Mexico', 'Moldova', 'Monaco',
  'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nepal',
  'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Macedonia',
  'Norway', 'Oman', 'Pakistan', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
  'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
  'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
  'Slovakia', 'Slovenia', 'Somalia', 'South Africa', 'South Korea', 'South Sudan',
  'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
  'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Togo', 'Trinidad and Tobago',
  'Tunisia', 'Turkey', 'Turkmenistan', 'Uganda', 'Ukraine', 'United Arab Emirates',
  'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Venezuela', 'Vietnam',
  'Yemen', 'Zambia', 'Zimbabwe', 'Sao Tome and Principe',
];

// What people actually type, mapped to the stored form above.
const COUNTRY_ALIASES = {
  uk: 'United Kingdom', 'u.k.': 'United Kingdom', 'great britain': 'United Kingdom',
  britain: 'United Kingdom', england: 'United Kingdom', scotland: 'United Kingdom',
  wales: 'United Kingdom',
  usa: 'United States', 'u.s.a.': 'United States', us: 'United States',
  'u.s.': 'United States', america: 'United States', 'united states of america': 'United States',
  uae: 'United Arab Emirates', 'u.a.e.': 'United Arab Emirates', emirates: 'United Arab Emirates',
  holland: 'Netherlands', 'the netherlands': 'Netherlands', nederland: 'Netherlands',
  saudi: 'Saudi Arabia', 'saudi arabia': 'Saudi Arabia',
  korea: 'South Korea', 'republic of korea': 'South Korea',
  czechia: 'Czech Republic',
  'sao tome': 'Sao Tome and Principe',
  bharat: 'India',
};

const COUNTRY_BY_KEY = (() => {
  const m = new Map();
  for (const name of COUNTRY_NAMES) m.set(name.toLowerCase(), name);
  for (const [alias, name] of Object.entries(COUNTRY_ALIASES)) m.set(alias, name);
  return m;
})();

/** The canonical country name for a free-text segment, or '' if it is not one. */
export const matchCountry = (segment) =>
  COUNTRY_BY_KEY.get(String(segment || '').trim().toLowerCase().replace(/\.$/, '')) || '';

/**
 * True for a value that is a map reference rather than a place name.
 *
 * Covers both the ways they arrive: DMS with degree marks (18°49'10.0"N
 * 73°31'01.6"E) and a bare decimal pair (19.076, 72.877). The decimal test is
 * anchored to the whole string so that a place name which merely contains
 * numbers is not caught by it.
 */
export const looksLikeCoordinates = (text) => {
  const t = String(text || '').trim();
  if (!t) return false;
  if (/[°]/.test(t)) return true;
  if (/^-?\d{1,3}(\.\d+)?\s*[°º]?\s*[NS]?\s*[,;]\s*-?\d{1,3}(\.\d+)?\s*[°º]?\s*[EW]?$/i.test(t)) return true;
  return false;
};

/**
 * Splits an inquiry's Location text into `{ city, country }`.
 *
 * Returns blanks rather than throwing for anything it cannot read, so a caller
 * can always spread the result into a create payload.
 */
export const parseInquiryLocation = (text) => {
  const raw = String(text || '').trim();
  const empty = { city: '', country: '' };
  if (!raw || looksLikeCoordinates(raw)) return empty;

  const segments = raw.split(',').map(s => s.trim()).filter(Boolean);
  if (!segments.length) return empty;

  const country = matchCountry(segments[segments.length - 1]);

  // A location that is nothing but a country name ("Colombia", "Cyprus") gives
  // a country and no city, which is exactly what was known.
  if (country && segments.length === 1) return { city: '', country };

  const city = segments[0];
  // No letters means it is a plot number or a stray figure, not a place.
  if (!/[a-z]/i.test(city)) return { city: '', country };
  return { city, country };
};

export default parseInquiryLocation;
